import {inject} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';

import {map, take} from 'rxjs/operators';

import {AppRoutes} from 'frontend/constants/routes';
import {GcpIapIdentity, IdentityService} from 'frontend/services/identity_service';

/**
 * Checks if the user's identity belongs to an authorized internal employee (@google.com or @tapestry.energy).
 * Strips any identity prefixes (e.g. 'accounts.google.com:') and enforces strict domain matching.
 */
export function validateInternalIdentity(identityResponse: GcpIapIdentity | null): boolean {
  const email = identityResponse?.email;
  if (!email) {
    return false;
  }

  const emailClean = email.substring(email.lastIndexOf(':') + 1).toLowerCase();
  return (
    !!emailClean && (emailClean.endsWith('@google.com') || emailClean.endsWith('@tapestry.energy'))
  );
}

/**
 * Route guard that ensures only internal users (@google.com or @tapestry.energy)
 * can access the protected route. Redirects unauthorized users to /access-denied.
 */
export const internalAccessGuard: CanActivateFn = () => {
  const identityService = inject(IdentityService);
  const router = inject(Router);

  return identityService.fetchGcpIapIdentity().pipe(
    take(1),
    map((identity: GcpIapIdentity | null): boolean => {
      if (validateInternalIdentity(identity)) {
        return true;
      }

      router.navigate([AppRoutes.ACCESS_DENIED.path]);
      return false;
    }),
  );
};
